import type { Transcript } from '../types/transcript.js';

/**
 * A single action item extracted from the transcript.
 */
export interface ActionItem {
  /** The action item text */
  text: string;
  /** Detected assignee name (if found in text) */
  assignee?: string;
  /** Detected due date (if found in text) */
  dueDate?: string;
  /** Original line number in action_items string (1-indexed) */
  lineNumber: number;
  /** Related sentence from transcript (if AIFilter.task matched) */
  sourceSentence?: {
    speakerName: string;
    text: string;
    startTime: number;
  };
}

/**
 * Result of action item extraction.
 */
export interface ActionItemsResult {
  /** Extracted action items */
  items: ActionItem[];
  /** Total count of action items */
  totalItems: number;
  /** Items with detected assignees */
  assignedItems: number;
  /** Items with detected due dates */
  datedItems: number;
  /** Unique assignees found */
  assignees: string[];
}

/**
 * Options for action item extraction.
 */
export interface ActionItemOptions {
  /**
   * Attempt to detect assignees from text patterns (default: true).
   * Looks for patterns like "@Alice", "Alice:", "assigned to Alice".
   */
  detectAssignees?: boolean;
  /**
   * Attempt to detect due dates from text patterns (default: true).
   * Looks for patterns like "by Friday", "due 2024-01-15", "EOD".
   */
  detectDueDates?: boolean;
  /**
   * Match action items with AIFilter.task in sentences (default: false).
   * When enabled, correlates summary items with source sentences.
   */
  includeSourceSentences?: boolean;
  /**
   * Known participant names for better assignee matching (default: []).
   * Improves accuracy by limiting to actual meeting participants.
   */
  participantNames?: string[];
}

// Assignee detection patterns in priority order (highest first)
const ASSIGNEE_PATTERNS: Array<{ pattern: RegExp; group: number }> = [
  { pattern: /@(\w+)/i, group: 1 }, // @Alice
  { pattern: /^(\w+):/i, group: 1 }, // Alice: at start
  { pattern: /assigned to (\w+)/i, group: 1 }, // assigned to Alice
  { pattern: /(\w+) will\b/i, group: 1 }, // Alice will
  { pattern: /(\w+) to\b/i, group: 1 }, // Alice to (do something)
  { pattern: /\s-\s*(\w+)$/i, group: 1 }, // ... - Alice
];

// Due date detection patterns
const DUE_DATE_PATTERNS: Array<{ pattern: RegExp; group: number }> = [
  { pattern: /by (monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i, group: 1 },
  { pattern: /by (tomorrow|today)/i, group: 1 },
  { pattern: /by (EOD|end of day)/i, group: 1 },
  { pattern: /by (EOW|end of week)/i, group: 1 },
  { pattern: /due (\d{4}-\d{2}-\d{2})/i, group: 1 },
  { pattern: /due (jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s+\d+/i, group: 0 },
  { pattern: /by (\d{1,2}\/\d{1,2})/i, group: 1 },
];

// List item prefixes to strip
const LIST_PREFIX_PATTERN = /^(?:[-•*]|\d+\.)\s*/;

// Section header pattern: **Name** or **Multiple Words**
// Used by Fireflies to group action items by assignee
const SECTION_HEADER_PATTERN = /^\*\*(.+)\*\*$/;

/**
 * Extract structured action items from a transcript.
 *
 * Parses the action_items field from the transcript summary into
 * structured data with optional assignee and due date detection.
 *
 * **Note:** These action items are generated by Fireflies' AI processing
 * during transcription. For custom action item extraction or different
 * interpretation logic, consider running your own LLM calls on the
 * transcript sentences directly.
 *
 * @param transcript - The transcript to extract action items from
 * @param options - Extraction options
 * @returns Structured action items with metadata
 *
 * @example
 * ```typescript
 * const result = extractActionItems(transcript);
 * for (const item of result.items) {
 *   console.log(`${item.text} -> ${item.assignee ?? 'unassigned'}`);
 * }
 * ```
 */
export function extractActionItems(
  transcript: Transcript,
  options: ActionItemOptions = {}
): ActionItemsResult {
  const actionItemsText = transcript.summary?.action_items;
  if (!actionItemsText || actionItemsText.trim().length === 0) {
    return emptyResult();
  }

  const config: ParseConfig = {
    detectAssignees: options.detectAssignees ?? true,
    detectDueDates: options.detectDueDates ?? true,
    includeSourceSentences: options.includeSourceSentences ?? false,
    participantNames: options.participantNames ?? [],
  };

  const taskSentences = config.includeSourceSentences ? buildTaskSentenceLookup(transcript) : [];
  const lines = actionItemsText.split(/\n/);

  return parseAllLines(lines, config, taskSentences);
}

function parseAllLines(
  lines: string[],
  config: ParseConfig,
  taskSentences: TaskSentence[]
): ActionItemsResult {
  const items: ActionItem[] = [];
  const assigneeSet = new Set<string>();
  let currentSectionAssignee: string | undefined;

  for (let i = 0; i < lines.length; i++) {
    const result = processLine(lines[i], i + 1, config, taskSentences, currentSectionAssignee);
    if (result.type === 'header') {
      currentSectionAssignee = result.assignee;
    } else if (result.type === 'item' && result.item) {
      items.push(result.item);
      if (result.item.assignee) {
        assigneeSet.add(result.item.assignee);
      }
    }
  }

  return buildResult(items, assigneeSet);
}

type LineResult =
  | { type: 'skip' }
  | { type: 'header'; assignee: string | undefined }
  | { type: 'item'; item: ActionItem };

function processLine(
  line: string | undefined,
  lineNumber: number,
  config: ParseConfig,
  taskSentences: TaskSentence[],
  sectionAssignee: string | undefined
): LineResult {
  if (!line) return { type: 'skip' };

  const trimmed = line.trim();
  if (trimmed.length === 0) return { type: 'skip' };

  // Check for section header (**Name**)
  const headerMatch = trimmed.match(SECTION_HEADER_PATTERN);
  if (headerMatch?.[1]) {
    const headerName = headerMatch[1];
    const assignee = headerName.toLowerCase() === 'unassigned' ? undefined : headerName;
    return { type: 'header', assignee };
  }

  const item = parseLine(line, lineNumber, config, taskSentences, sectionAssignee);
  if (item) {
    return { type: 'item', item };
  }

  return { type: 'skip' };
}

interface ParseConfig {
  detectAssignees: boolean;
  detectDueDates: boolean;
  includeSourceSentences: boolean;
  participantNames: string[];
}

function parseLine(
  line: string | undefined,
  lineNumber: number,
  config: ParseConfig,
  taskSentences: TaskSentence[],
  sectionAssignee?: string
): ActionItem | null {
  if (!line) return null;

  const trimmed = line.trim();
  if (trimmed.length === 0) return null;

  const text = trimmed.replace(LIST_PREFIX_PATTERN, '');
  if (text.length === 0) return null;

  // Inline assignee detection takes priority over section assignee
  const inlineAssignee = config.detectAssignees
    ? detectAssignee(text, config.participantNames)
    : undefined;
  const assignee = inlineAssignee ?? sectionAssignee;

  const dueDate = config.detectDueDates ? detectDueDate(text) : undefined;
  const sourceSentence = config.includeSourceSentences
    ? findSourceSentence(text, taskSentences)
    : undefined;

  return { text, assignee, dueDate, lineNumber, sourceSentence };
}

function buildResult(items: ActionItem[], assigneeSet: Set<string>): ActionItemsResult {
  return {
    items,
    totalItems: items.length,
    assignedItems: items.filter((i) => i.assignee !== undefined).length,
    datedItems: items.filter((i) => i.dueDate !== undefined).length,
    assignees: Array.from(assigneeSet),
  };
}

function emptyResult(): ActionItemsResult {
  return {
    items: [],
    totalItems: 0,
    assignedItems: 0,
    datedItems: 0,
    assignees: [],
  };
}

function detectAssignee(text: string, participantNames: string[]): string | undefined {
  const participantSet = new Set(participantNames.map((n) => n.toLowerCase()));
  const filterByParticipants = participantSet.size > 0;

  for (const { pattern, group } of ASSIGNEE_PATTERNS) {
    const match = text.match(pattern);
    if (match?.[group]) {
      const name = match[group];
      // If participantNames provided, only match against those names
      if (filterByParticipants) {
        if (participantSet.has(name.toLowerCase())) {
          return name;
        }
        // Continue checking other patterns
        continue;
      }
      return name;
    }
  }

  return undefined;
}

function detectDueDate(text: string): string | undefined {
  for (const { pattern, group } of DUE_DATE_PATTERNS) {
    const match = text.match(pattern);
    if (match) {
      // For "due Jan 15" pattern (group 0), extract the full date part
      if (group === 0 && match[0]) {
        const fullMatch = match[0];
        // Extract just the date portion (e.g., "Jan 15" from "due Jan 15")
        const dateMatch = fullMatch.match(
          /(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s+\d+/i
        );
        if (dateMatch?.[0]) {
          return dateMatch[0];
        }
      }
      if (match[group]) {
        return match[group];
      }
    }
  }

  return undefined;
}

interface TaskSentence {
  text: string;
  task: string;
  speakerName: string;
  startTime: number;
}

function buildTaskSentenceLookup(transcript: Transcript): TaskSentence[] {
  const sentences = transcript.sentences ?? [];
  const result: TaskSentence[] = [];

  for (const sentence of sentences) {
    const task = sentence.ai_filters?.task;
    if (task) {
      result.push({
        text: sentence.text,
        task: task.toLowerCase(),
        speakerName: sentence.speaker_name,
        startTime: Number.parseFloat(sentence.start_time),
      });
    }
  }

  return result;
}

function findSourceSentence(
  actionItemText: string,
  taskSentences: TaskSentence[]
): ActionItem['sourceSentence'] | undefined {
  const normalizedItem = actionItemText.toLowerCase();

  // Look for task sentences that have significant word overlap
  for (const sentence of taskSentences) {
    // Check if the task text is contained in the action item or vice versa
    if (normalizedItem.includes(sentence.task) || sentence.task.includes(normalizedItem)) {
      return {
        speakerName: sentence.speakerName,
        text: sentence.text,
        startTime: sentence.startTime,
      };
    }

    // Also check for significant word overlap
    const itemWords = new Set(normalizedItem.split(/\s+/).filter((w) => w.length > 3));
    const taskWords = sentence.task.split(/\s+/).filter((w) => w.length > 3);
    const matchingWords = taskWords.filter((w) => itemWords.has(w));

    if (taskWords.length > 0 && matchingWords.length / taskWords.length >= 0.5) {
      return {
        speakerName: sentence.speakerName,
        text: sentence.text,
        startTime: sentence.startTime,
      };
    }
  }

  return undefined;
}
